import React from 'react'
import { useLocation, useNavigate } from 'react-router'
import cx from 'classnames'
import './BackLink.scss'

export interface BackLinkProps {
  /** Route to fall back to when there is no in-app history to pop. */
  to: string;
  label?: string;
  className?: string;
}

/**
 * Minimal "back" navigation link shared by the detail pages — a clean
 * arrow-left icon + grey label, no border/chrome.
 *
 * Renders a real anchor so the fallback route is visible to the browser —
 * middle-click, cmd/ctrl-click and "Open in new tab" all work. A plain click
 * pops history instead, which keeps the caller's scroll position and filters.
 */
const BackLink: React.FC<BackLinkProps> = ({ to, label = 'Back', className }) => {
  const navigate = useNavigate()
  const location = useLocation()

  // React Router stamps the first entry of a session with key 'default'. If we
  // are still on it, there is nothing of ours to pop back to — a direct visit,
  // a refresh, or a fresh tab — so send the user to `to` instead of bouncing
  // them out of the app.
  const canPopHistory = location.key !== 'default'

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // Let the browser own modified clicks so they open `to` in a new tab/window.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.altKey ||
      event.button !== 0
    ) {
      return
    }
    event.preventDefault()
    if (canPopHistory) navigate(-1)
    else navigate(to)
  }

  return (
    <a
      href={to}
      onClick={handleClick}
      className={cx('back-link', className)}
      data-testid="back-link"
    >
      <svg
        className="back-link__arrow"
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M19 12H5" />
        <path d="M12 19l-7-7 7-7" />
      </svg>
      {label}
    </a>
  )
}

export default BackLink
